import React, { memo, useMemo, useRef } from 'react';

import useIframeAutoHeight from '@hooks/useIframeAutoHeight';
import useImageAspectRatio from '@hooks/useImageAspectRatio';
import useServerSideRendering from '@hooks/useServerSideRendering';

const DEFAULT_HEIGHT = 600;

interface SelfHostedWrapperProps {
  fallbackImageURL: string;
  height?: number | null;
  sourceUrl: string | null;
  title: string;
}

const SelfHostedWrapper = ({
  fallbackImageURL,
  height,
  sourceUrl,
  title,
}: SelfHostedWrapperProps) => {
  const { aspectRatio, imgRef, onLoad } = useImageAspectRatio();
  const hydrated = useServerSideRendering();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  let expectedOrigin: string | null = null;
  if (sourceUrl) {
    try {
      expectedOrigin = new URL(sourceUrl).origin;
    } catch {
      expectedOrigin = null;
    }
  }

  useIframeAutoHeight(iframeRef, hydrated ? expectedOrigin : null);

  const aspectRatioStyle = useMemo(() => {
    return aspectRatio ? { aspectRatio } : undefined;
  }, [aspectRatio]);

  const fallbackImage = useMemo(() => {
    return (
      <img
        alt={title}
        className="h-auto w-full"
        onLoad={onLoad}
        ref={imgRef}
        src={fallbackImageURL}
        style={aspectRatioStyle}
      />
    );
  }, [fallbackImageURL, aspectRatioStyle, imgRef, onLoad, title]);

  if (!sourceUrl) {
    return <>{fallbackImage}</>;
  }

  return !hydrated ? (
    <>{fallbackImage}</>
  ) : (
    <figure
      aria-live="polite"
      className="relative z-10 m-0 h-auto w-full *:bg-white dark:*:bg-white"
      style={aspectRatioStyle}
    >
      <iframe
        ref={iframeRef}
        className="block w-full"
        frameBorder="0"
        height={height ?? DEFAULT_HEIGHT}
        referrerPolicy="no-referrer"
        sandbox="allow-scripts allow-same-origin"
        scrolling="no"
        src={sourceUrl}
        title={title}
        width="100%"
      />
      <noscript>{fallbackImage}</noscript>
    </figure>
  );
};

export default memo(SelfHostedWrapper);
